import React from 'react';
import { Card } from '../../CompositeComponents/Card/Card';
import { BarChart } from '../BarChart/BarChart';
import { currencySymbol, formatMoneyParts } from '../../../utils/currency';
import type { InventoryValueWidgetProps } from './types';

// ─── InventoryValueWidget ─────────────────────────────────────────────────────

export function InventoryValueWidget({
  totalValue,
  categories,
  title = 'Inventory Value',
  currency = 'INR',
  className,
}: InventoryValueWidgetProps) {
  const fmtPaise = (paise: number): string =>
    formatMoneyParts(paise, currency, { maximumFractionDigits: 0 });
  const barData = categories.map((c) => ({ label: c.label, value: c.value, color: c.color }));

  return (
    <Card className={className}>
      <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-1">{title}</p>
      <p className="text-2xl font-bold text-brand-600 dark:text-brand-400 mb-4" aria-live="polite">
        {fmtPaise(totalValue)}
      </p>

      {categories.length > 0 ? (
        <BarChart data={barData} yAxisLabel={`Value (${currencySymbol(currency)})`} />
      ) : (
        <p className="text-sm text-neutral-400 dark:text-neutral-500 text-center py-6">
          No category data
        </p>
      )}

      {/* Category breakdown list */}
      {categories.length > 0 && (
        <ul className="mt-3 space-y-1">
          {categories.map((c, i) => (
            <li key={i} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-neutral-600 dark:text-neutral-300">
                {c.color && (
                  <span
                    className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: c.color }}
                  />
                )}
                {c.label}
              </span>
              <span className="font-medium text-neutral-700 dark:text-neutral-200">
                {fmtPaise(c.value)}
                {totalValue > 0 && (
                  <span className="ml-1 text-neutral-400">
                    ({((c.value / totalValue) * 100).toFixed(0)}%)
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
